import { Table } from "antd";
import { MODIFY_MODIFICATION_OPTIONS } from "components/CC/Nodes/ModifyFieldNode/const";
import { ModifyModification, ModifyStepSummary } from "components/CC/RunHistory/StepDetails/utils/types";
import { DrawerCollapsible } from "components/UI/DrawerCollapsible/DrawerCollapsible";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC } from "react";

import type { ColumnsType } from "antd/es/table";

interface ModifyRow {
    key: string;
    field: string;
    before: string;
    after: string;
    modifications: ModifyModification[];
}

export interface StepDetailModifyProps {
    extractedData: ModifyStepSummary;
}

const formatValue = (value: unknown): string => {
    if (value == null || value === "") return "-";
    return typeof value === "object" ? JSON.stringify(value) : String(value);
};

export const StepDetailModify: FC<StepDetailModifyProps> = ({ extractedData }) => {
    const { t } = usePaldockTranslation("drawers.step-detail.modify");
    const { t: tCC } = usePaldockTranslation("components.connection-creator");

    const translateModificator = (modificator: string): string => {
        const option = MODIFY_MODIFICATION_OPTIONS.find((opt) => opt.value === modificator);
        return option ? tCC(option.labelKey) : modificator;
    };

    const rows: ModifyRow[] = extractedData.changes.map((change, index) => ({
        key: String(index),
        field: change.field,
        before: formatValue(change.before),
        after: formatValue(change.after),
        modifications: change.modifications,
    }));

    const columns: ColumnsType<ModifyRow> = [
        { title: t("parameter"), dataIndex: "field", key: "field" },
        { title: t("before"), dataIndex: "before", key: "before" },
        { title: t("after"), dataIndex: "after", key: "after" },
    ];

    const modificationColumns: ColumnsType<ModifyModification> = [
        { title: t("modificator"), dataIndex: "modificator", key: "modificator", render: translateModificator },
        { title: t("params"), dataIndex: "params", key: "params", render: formatValue },
        { title: t("before"), dataIndex: "before", key: "before", render: formatValue },
        { title: t("after"), dataIndex: "after", key: "after", render: formatValue },
    ];

    return (
        <DrawerCollapsible title={t("data")}>
            <Table
                columns={columns}
                dataSource={rows}
                pagination={false}
                size="small"
                bordered={false}
                rowClassName={(row) => (row.modifications.length > 0 ? "cursor-pointer" : "")}
                expandable={{
                    expandRowByClick: true,
                    expandedRowRender: (row) =>
                        row.modifications.length > 0 ? (
                            <Table
                                columns={modificationColumns}
                                dataSource={row.modifications.map((m, i) => ({ ...m, key: String(i) }))}
                                pagination={false}
                                size="small"
                                bordered={false}
                                showHeader={true}
                            />
                        ) : null,
                    rowExpandable: (row) => row.modifications.length > 0,
                }}
            />
        </DrawerCollapsible>
    );
};
